import { useParams, Outlet, Navigate, useLocation } from "react-router-dom";
import { useI18n, type Lang } from "@/i18n/I18nContext";
import { useEffect, useMemo } from "react";
import { useVisitorPresence } from "@/hooks/useRealtimeVisitors";
import { canonicalPathFor } from "@/lib/routeSlugs";

const VALID_LANGS = ["pl", "en", "de", "fr", "it"];

function getDetectedLang(): string {
  const saved = localStorage.getItem("lang");
  if (saved && VALID_LANGS.includes(saved)) return saved;
  const browserLang = navigator.language?.slice(0, 2);
  if (browserLang === "pl") return "pl";
  if (browserLang === "de") return "de";
  if (browserLang === "fr") return "fr";
  if (browserLang === "it") return "it";
  return "en";
}

/** Wraps all public routes under /:lang */
export default function LangLayout() {
  const { lang: urlLang, "*": rest } = useParams<{ lang: string; "*": string }>();
  const { setLang } = useI18n();
  const location = useLocation();

  useEffect(() => {
    if (urlLang && VALID_LANGS.includes(urlLang)) {
      setLang(urlLang as Lang);
      document.documentElement.lang = urlLang;
    }
  }, [urlLang, setLang]);

  const pageNameMap: Record<string, string> = {
    "": "Strona główna",
    "koszyk": "Koszyk",
    "zamowienie": "Checkout",
    "blog": "Blog",
    "kontakt": "Kontakt",
    "faq": "FAQ",
    "folie": "Folie",
    "portfolio": "Portfolio",
    "konto": "Konto",
  };

  const currentPage = useMemo(() => {
    const seg = rest?.split("/")[0] || "";
    return pageNameMap[seg] || seg || "Strona główna";
  }, [rest]);

  useVisitorPresence(currentPage);

  if (!urlLang || !VALID_LANGS.includes(urlLang)) {
    // urlLang is actually a path segment (e.g. "blog"), rebuild full legacy path
    const fullPath = urlLang ? `/${urlLang}${rest ? `/${rest}` : ""}` : "/";
    return <Navigate to={`/${getDetectedLang()}${fullPath}${location.search}${location.hash}`} replace />;
  }

  // Enforce one canonical URL per language: non-canonical slug variants redirect.
  const canonicalPath = canonicalPathFor(location.pathname, urlLang);
  if (canonicalPath) {
    const norm = (p: string) => p.replace(/\/+$/, "") || "/";
    if (norm(canonicalPath) !== norm(location.pathname)) {
      return <Navigate to={`${canonicalPath}${location.search}${location.hash}`} replace />;
    }
  }

  return <Outlet />;
}

/** Handles root "/" – always resolves to the default (pl) version, identically
 *  for every visitor (no user-agent sniffing, no language detection).
 *  Keeps "/" a stable x-default target. */
export function GeoRedirect() {
  return <Navigate to="/pl/" replace />;
}

/** Handles legacy URLs without lang prefix (e.g. /blog → /pl/blog) */
export function LegacyRedirect() {
  const location = useLocation();
  const detected = getDetectedLang();
  return <Navigate to={`/${detected}${location.pathname}${location.search}${location.hash}`} replace />;
}
